import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { portalPaths, UrlParams } from '@provision/portal-routes';
import { useCallback, useMemo, useState } from 'react';
import { Conversation, useCurrentUserProfile, useMergeConversation } from 'src/graphql';
import { Modals, useModalMethods, useModalState } from 'src/store/modals';
import { useNotifications } from 'src/utils/hooks';
import { useNavigateRoute, useRouteParams } from 'src/utils/routing';
import { ConversationSelector } from './conversation-selector';
import { MergeTargetSuggestions } from './merge-target-suggestions';

export const MergeConversationModal = () => {
  const { closeModal } = useModalMethods(Modals.MergeConversationModal);
  const { isOpened, modalProps } = useModalState(Modals.MergeConversationModal);
  const [conversationTarget, setConversationTarget] = useState<Conversation | null>(null);
  const { customViewId } = useRouteParams(UrlParams.customViewId);
  const { mergeConversation } = useMergeConversation();

  const { conversation } = modalProps ?? {};

  const { showErrorAlert, showSuccessAlert } = useNotifications();

  const { navigate } = useNavigateRoute();

  const { user } = useCurrentUserProfile();

  const selectConversation = useCallback(
    (conversation: Conversation) => {
      navigate(
        portalPaths.entity.all({
          entityName: 'conversations',
          customViewId,
          id: conversation?.friendlyId,
        }),
      );
    },
    [navigate, customViewId],
  );

  const closeMergeModal = useCallback(() => {
    closeModal();
    setConversationTarget(null);
  }, [closeModal]);

  const handleMergeConversations = useCallback(async () => {
    if (!conversation?.id) {
      showErrorAlert(`Invalid conversation target ${conversation?.id}`);

      return;
    }

    if (!conversationTarget?.id) {
      showErrorAlert(`Invalid conversation origin ${conversationTarget?.id}`);

      return;
    }

    closeMergeModal();

    const response = await mergeConversation({
      conversationTargetId: conversationTarget?.id,
      friendlyConversationTargetId: conversationTarget.friendlyId,
      conversationOriginId: conversation?.id,
      friendlyConversationOriginId: conversation.friendlyId,
      user,
    });

    if (!response) {
      return;
    }

    if (response.error) {
      console.error(response.error);
      showErrorAlert(response.error);
      return;
    }

    showSuccessAlert('Conversations were merged');
    setConversationTarget(null);
    selectConversation(conversationTarget);
  }, [
    closeMergeModal,
    conversation,
    conversationTarget,
    mergeConversation,
    selectConversation,
    showErrorAlert,
    showSuccessAlert,
    user,
  ]);

  const filters = useMemo(() => (conversation ? [conversation] : []), [conversation]);

  return (
    <Dialog open={isOpened} onClose={closeMergeModal} fullWidth>
      <DialogTitle>
        <Stack direction="row" spacing={2}>
          <Typography>Merge Conversations</Typography>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <ConversationSelector
            conversation={conversation}
            deletable={false}
            title="Origin Conversation"
          />
          <ConversationSelector
            deletable
            title="Target Conversation"
            filters={filters}
            partnerId={conversation?.partner?.id}
            conversation={conversationTarget}
            setConversation={setConversationTarget}
          />
          {conversation && !conversationTarget ? (
            <MergeTargetSuggestions origin={conversation} onSelect={setConversationTarget} />
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          autoFocus
          onClick={handleMergeConversations}
          disabled={!conversation || !conversationTarget}
        >
          Merge Conversations
        </Button>
      </DialogActions>
    </Dialog>
  );
};
